import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Chip, IconButton, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate, useParams } from 'react-router';
import { MAX_VISIBLE } from '../utils/transcriptUtils';

const useStyles = makeStyles((theme) => ({
  box: {
    width: '100%',
    height: '100%',
    borderRadius: '5px',
    cursor: 'pointer',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    border: '1px solid #eee',
    paddingRight: '8px',
    paddingLeft: '8px',
    marginRight: '16px',
  },
  listItemIconBox: {
    width: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    padding: '8px',
    marginRight: '8px',
  },
  listItem: {
    padding: '0 !important',
    marginBottom: '8px',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  listItemActive: {
    border: '2px solid #7D5AE8',
  },
  filterCountBadge: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50% !important',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: '0.75rem',
    border: '2px solid white',
    zIndex: 99,
  },
}));

const TranscriptItem = ({
  transcript,
  index,
  isActive,
  getTranscriptName,
  assignedTags = [],
  onMenuClick,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();

  const transcriptIndex = transcript?.transcript_index;
  const fallbackName = transcriptIndex
    ? `Transcript ${transcriptIndex}`
    : `Transcript ${getTranscriptName(transcript?.id)}`;
  const displayName =
    transcript?.name && transcript?.name !== 'Interview started' ? transcript?.name : fallbackName;

  const status = (transcript?.interview_status || '').toLowerCase();
  const statusMeta = (() => {
    if (status === 'active') {
      return {
        label: 'Active',
        color: 'info',
        tooltip:
          'This session might still be active. If this session was discarded, it will auto-resolve to complete or incomplete within an hour.',
      };
    }
    if (status === 'draft') {
      return {
        label: 'Draft',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    if (status === 'incomplete') {
      return {
        label: 'Incomplete',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    if (status === 'closed') {
      return {
        label: 'Complete',
        color: 'success',
        tooltip: '',
      };
    }
    if (transcript?.is_complete === false) {
      return {
        label: 'Incomplete',
        color: 'warning',
        tooltip:
          'This session was not marked as complete by the AI interviewer. You can manually mark it as complete using the ... options if you want this to be part of any analysis.',
      };
    }
    return { label: 'Complete', color: 'success', tooltip: '' };
  })();

  const handleTranscriptClick = () => {
    navigate(`/interview/${id}/transcripts/${transcript.id}`, {
      replace: true,
      state: {
        transcriptName: displayName,
      },
    });
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    // Create transcript object with name for delete confirmation
    const transcriptWithName = {
      ...transcript,
      name: displayName,
    };
    onMenuClick && onMenuClick(event, transcriptWithName);
  };

  return (
    <>
      <ListItem className={`${classes.listItem}`} onClick={handleTranscriptClick}>
        <Box className={classes.listItemIconBox}>
          <Typography variant="body2">{transcriptIndex || index + 1}.</Typography>
        </Box>
        <Box className={`${classes.box}  ${isActive ? classes.listItemActive : ''}`}>
          <ListItemText
            component="div"
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '140px',
                    fontWeight: 600,
                  }}
                >
                  {displayName}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="More options"
                    data-transcript-id={transcript.id}
                    onClick={handleMenuClick}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            }
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <>
                {statusMeta.tooltip ? (
                  <Tooltip title={statusMeta.tooltip} arrow>
                    <Chip
                      label={statusMeta.label}
                      color={statusMeta.color}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': {
                          paddingX: 0.5,
                        },
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    label={statusMeta.label}
                    color={statusMeta.color}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      '& .MuiChip-label': {
                        paddingX: 0.5,
                      },
                      mt: 0.5,
                    }}
                  />
                )}
                {assignedTags?.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {assignedTags.slice(0, MAX_VISIBLE).map((tag) => (
                      <Typography
                        key={`${tag.tag_key_id}-${tag.tag_value_id}`}
                        variant="caption"
                        sx={{
                          backgroundColor: '#f0f0f0',
                          color: '#666',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                        }}
                      >
                        {tag?.tag_key}: {tag?.values?.map((value) => value.value_key).join(', ')}
                      </Typography>
                    ))}
                    {assignedTags.length > MAX_VISIBLE && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          fontSize: '0.7rem',
                          fontStyle: 'italic',
                        }}
                      >
                        +{assignedTags.length - MAX_VISIBLE} more
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            }
          />
        </Box>
      </ListItem>
    </>
  );
};

export default TranscriptItem;
